import { registerServiceWorker, getFCMToken } from '@/components/firebaseConfig';
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { saveFCMToken } from '@/lib/fcm';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOptimizedGeolocation } from '@/hooks/useOptimizedGeolocation';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { LogIn, X, List, Plus, Swords, MapPin, Bell } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import ChallengeNotification from '@/components/ChallengeNotification';
import { useChallengeState } from '@/components/useChallengeState';

const DEFAULT_CENTER = [2.50619, 48.93704]; // Seine-Saint-Denis
const DEFAULT_ZOOM = 12;
const TAB_BAR_OFFSET = 'calc(3rem + env(safe-area-inset-bottom))'; // h-12 + safe area
const LOCATION_BTN_BOTTOM = `calc(${TAB_BAR_OFFSET} + 12px)`;
const OPEN_DUELS_BTN_BOTTOM = `calc(${TAB_BAR_OFFSET} + 12px + 52px)`; // 40px button + 12px gap
const OPEN_DUEL_DURATION_MS = 2 * 60 * 60 * 1000;

// sessionStorage helpers for map state persistence
const MAP_STATE_KEY = 'zno:mapState';

function saveMapState(map) {
  try {
    const center = map.getCenter();
    const state = {
      lng: center.lng,
      lat: center.lat,
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch()
    };
    sessionStorage.setItem(MAP_STATE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage not available
  }
}

function getStoredMapState() {
  try {
    const stored = sessionStorage.getItem(MAP_STATE_KEY);
    if (stored) {
      const state = JSON.parse(stored);
      if (state.lng && state.lat && state.zoom) {
        return state;
      }
    }
  } catch {
    // sessionStorage not available
  }
  return null;
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

function getOpenStartDate(duel) {
  return (
    parseDate(duel.refused_at) ||
    parseDate(duel.opened_at) ||
    parseDate(duel.accept_deadline_at) ||
    parseDate(duel.slot_selection_deadline_at) ||
    parseDate(duel.updated_date) ||
    parseDate(duel.created_at) ||
    parseDate(duel.created_date)
  );
}

function getOpenExpiresAt(duel) {
  const explicit = parseDate(duel.open_expires_at);
  if (explicit) return explicit;
  const start = getOpenStartDate(duel);
  if (!start) return null;
  return new Date(start.getTime() + OPEN_DUEL_DURATION_MS);
}

export default function MapPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    unviewedChallenges,
    markChallengesAsViewed
  } = useChallengeState(user);

  const { getLocation } = useOptimizedGeolocation();
  const [userLocation, setUserLocation] = useState(null);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [showList, setShowList] = useState(false);
  const [mapboxToken, setMapboxToken] = useState(null);
  const [showChallengeNotif, setShowChallengeNotif] = useState(false);
  const [notifChallenge, setNotifChallenge] = useState(null);
  const [showFindDuelsHint, setShowFindDuelsHint] = useState(false);

  const mapContainer = useRef(null);
  const mapInstance = useRef(null);
  const hintTimerRef = useRef(null);

  /**
   * ------------------------------------------------------------------
   * Notifications: permission & "dismissed today" (anti-loop)
   * ------------------------------------------------------------------
   */
  useEffect(() => {
    const initPushOnce = async () => {
      const registration = await registerServiceWorker();
      if (!registration) return;

      if (!('Notification' in window)) return;
      if (!window.isSecureContext) return;

      if (Notification.permission === 'default') {
        const res = await Notification.requestPermission();
        if (res !== 'granted') return;
      }
      if (Notification.permission !== 'granted') return;

      const token = await getFCMToken(registration);
      if (!token) return;

      await saveFCMToken(token);
    };

    window.addEventListener('pointerdown', initPushOnce, { once: true });
    return () => window.removeEventListener('pointerdown', initPushOnce);
  }, []);

  useEffect(() => {
    return () => {
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    };
  }, []);

  const getDayKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  };

  const dismissedKey = (userId, challengeId) => `zno:dismissed:${userId}:${challengeId}`;

  const wasDismissedToday = (userId, challengeId) => {
    try {
      return localStorage.getItem(dismissedKey(userId, challengeId)) === getDayKey();
    } catch {
      return false;
    }
  };

  const setDismissedToday = (userId, challengeId) => {
    try {
      localStorage.setItem(dismissedKey(userId, challengeId), getDayKey());
    } catch {
      // no-op
    }
  };

  /**
   * ------------------------------------------------------------------
   * Data
   * ------------------------------------------------------------------
   */
  const queryClient = useQueryClient();

  const { data: playgrounds = [] } = useQuery({
    queryKey: ['playgrounds'],
    queryFn: async () => {
      const results = await base44.entities.Playground.filter({ is_active: true });
      return results;
    }
  });

  // Fetch OPEN duels count for badge
  const { data: openDuelsCount = 0, isLoading: isLoadingOpenDuelsCount } = useQuery({
    queryKey: ['openDuelsCount'],
    queryFn: async () => {
      if (!user) return 0;
      const duels = await base44.entities.CourtDuel.filter({ status: 'OPEN' });
      const now = new Date();
      return duels.filter((duel) => {
        const openExpiresAt = getOpenExpiresAt(duel);
        return openExpiresAt && openExpiresAt > now;
      }).length;
    },
    enabled: !!user,
    refetchInterval: 30000
  });

  /**
   * ------------------------------------------------------------------
   * One-time hint for "Find Duels" location / open-duels button
   * ------------------------------------------------------------------
   */
  useEffect(() => {
    if (!user) return;
    if (isLoadingOpenDuelsCount) return;

    const canShowAboveOpenDuels = openDuelsCount > 0;
    const canShowAboveLocation = !!userLocation;
    if (!canShowAboveOpenDuels && !canShowAboveLocation) return;

    try {
      const seenKey = 'zno:seenFindDuelsHint';
      const sessionKey = 'zno:findDuelsHintSessionTs';
      const seen = localStorage.getItem(seenKey) === 'true';
      if (seen) return;

      const sessionTsRaw = sessionStorage.getItem(sessionKey);
      const sessionTs = sessionTsRaw ? Number(sessionTsRaw) : 0;
      const recentlyShown = sessionTs && (Date.now() - sessionTs < 1500);

      // If StrictMode remounts quickly, allow showing again once.
      if (!recentlyShown) {
        sessionStorage.setItem(sessionKey, String(Date.now()));
      }

      // Defer to next frame to avoid StrictMode timing quirks in preview
      requestAnimationFrame(() => {
        setShowFindDuelsHint(true);
      });

      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      hintTimerRef.current = setTimeout(() => {
        setShowFindDuelsHint(false);
        try {
          localStorage.setItem(seenKey, 'true');
        } catch {
          // localStorage not available
        }
      }, 5000);
    } catch {
      // storage not available
    }
  }, [user, userLocation, openDuelsCount, isLoadingOpenDuelsCount]);

  // Unread notifications count
  const { data: notifUnreadCount = 0 } = useQuery({
    queryKey: ['notifUnreadCount'],
    queryFn: async () => {
      if (!user) return 0;
      const { count } = await supabase.from('in_app_notifications').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('is_read', false);
      return count || 0;
    },
    enabled: !!user,
    refetchInterval: 15000
  });

  // Real-time subscription to Playground changes
  useEffect(() => {
    if (!user) return;

    const unsubscribe = base44.entities.Playground.subscribe((event) => {
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
    });

    return () => unsubscribe();
  }, [user, queryClient]);

  const spotMap = useMemo(() => {
    const map = new Map();
    playgrounds.forEach((pg) => {
      if (!map.has(pg.spot_id)) map.set(pg.spot_id, []);
      map.get(pg.spot_id).push(pg);
    });
    return map;
  }, [playgrounds]);

  // Helper to open a playground by ID - navigates to court detail route
  const openPlaygroundById = useCallback((playgroundId) => {
    if (!playgroundId) return false;
    navigate(`/court/${playgroundId}`);
    return true;
  }, [navigate]);

  // Read ?selected= from URL on load - redirect to court detail
  useEffect(() => {
    const selectedId = searchParams.get('selected');
    if (selectedId) {
      navigate(`/court/${selectedId}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // Listen for custom event from push notifications (foreground)
  useEffect(() => {
    const handleOpenPlayground = (e) => {
      const playgroundId = e.detail?.playground_id || e.detail?.playgroundId;
      if (playgroundId) {
        openPlaygroundById(playgroundId);
      }
    };

    const handleSWMessage = (event) => {
      if (event.data?.type === 'ZNO_NOTIFICATION_CLICK') {
        const data = event.data?.data || {};
        const url = event.data?.url || '';
        const playgroundId = data.playground_id || (() => {
          try {
            const params = new URLSearchParams(url.split('?')[1] || '');
            return params.get('selected');
          } catch (_) { return null; }
        })();
        if (playgroundId) {
          openPlaygroundById(playgroundId);
        }
      }
    };

    window.addEventListener('zno:openPlayground', handleOpenPlayground);
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    return () => {
      window.removeEventListener('zno:openPlayground', handleOpenPlayground);
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, [openPlaygroundById]);

  // Expose global callbacks for Layout components
  useEffect(() => {
    window.__onPlaygroundSelect = (playground) => {
      if (playground?.id) {
        navigate(`/court/${playground.id}`);
      }
    };

    window.__onFindDuelsClick = () => {
      if (mapInstance.current && userLocation) {
        mapInstance.current.flyTo({ center: userLocation, zoom: 14 });
      }
    };

    return () => {
      delete window.__onPlaygroundSelect;
      delete window.__onFindDuelsClick;
    };
  }, [userLocation, navigate]);

  /**
   * ------------------------------------------------------------------
   * Challenge Notification (non-looping)
   * ------------------------------------------------------------------
   */
  useEffect(() => {
    if (!user || !mapboxToken) return;
    if (showChallengeNotif) return;
    if (!Array.isArray(unviewedChallenges) || unviewedChallenges.length === 0) return;
    if (showChallengeNotif || notifChallenge?.id) return;

    const nextChallenge = unviewedChallenges.find((c) => !wasDismissedToday(user.id, c.id));
    if (!nextChallenge) return;

    const timer = setTimeout(() => {
      setNotifChallenge(nextChallenge);
      setShowChallengeNotif(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [user, mapboxToken, unviewedChallenges, showChallengeNotif, notifChallenge]);

  /**
   * ------------------------------------------------------------------
   * Mapbox token
   * ------------------------------------------------------------------
   */
  useEffect(() => {
    const token = import.meta.env.VITE_MAPBOX_TOKEN;
    if (token) {
      setMapboxToken(token);
      mapboxgl.accessToken = token;
    }
  }, []);

  /**
   * ------------------------------------------------------------------
   * Geolocation
   * ------------------------------------------------------------------
   */
  useEffect(() => {
    getLocation({ useCache: true, timeout: 3000 })
      .then(location => {
        const mapLocation = [location.lng, location.lat];
        setUserLocation(mapLocation);
      })
      .catch(err => {
        console.warn('Location failed:', err);
        setUserLocation(null);
      });
  }, [getLocation]);

  /**
   * ------------------------------------------------------------------
   * Map init
   * ------------------------------------------------------------------
   */
  useEffect(() => {
    if (!mapboxToken || !mapContainer.current) return;

    // Restore map state from sessionStorage if available
    const storedState = getStoredMapState();
    const initialCenter = storedState ? [storedState.lng, storedState.lat] : (userLocation || DEFAULT_CENTER);
    const initialZoom = storedState?.zoom ?? DEFAULT_ZOOM;
    const initialBearing = storedState?.bearing ?? 0;
    const initialPitch = storedState?.pitch ?? 0;

    const newMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: initialCenter,
      zoom: initialZoom,
      bearing: initialBearing,
      pitch: initialPitch,
      attributionControl: false
    });

    // Save map state on moveend for tab-switch persistence
    newMap.on('moveend', () => {
      saveMapState(newMap);
    });

    // Gamified style
    newMap.on('load', () => {
      const layers = newMap.getStyle().layers;

      layers.forEach((layer) => {
        if (layer.type === 'background') {
          newMap.setPaintProperty(layer.id, 'background-color', '#0a1628');
        }

        if (layer.type === 'fill' && layer.id.includes('water')) {
          newMap.setPaintProperty(layer.id, 'fill-color', '#1a2a3e');
          newMap.setPaintProperty(layer.id, 'fill-opacity', 0.9);
        }

        if (layer.type === 'fill' && (layer.id.includes('park') || layer.id.includes('landuse'))) {
          newMap.setPaintProperty(layer.id, 'fill-color', '#1a2a3a');
          newMap.setPaintProperty(layer.id, 'fill-opacity', 0.3);
        }

        if (layer.type === 'line' && layer.id.includes('road')) {
          if (layer.id.includes('major') || layer.id.includes('primary') || layer.id.includes('motorway')) {
            newMap.setPaintProperty(layer.id, 'line-color', '#ff6b35');
            newMap.setPaintProperty(layer.id, 'line-width', 2);
            newMap.setPaintProperty(layer.id, 'line-opacity', 0.6);
          } else if (layer.id.includes('secondary') || layer.id.includes('tertiary')) {
            newMap.setPaintProperty(layer.id, 'line-color', '#ff8c42');
            newMap.setPaintProperty(layer.id, 'line-width', 1.2);
            newMap.setPaintProperty(layer.id, 'line-opacity', 0.4);
          } else {
            newMap.setPaintProperty(layer.id, 'line-color', '#3a4a5a');
            newMap.setPaintProperty(layer.id, 'line-width', 0.8);
            newMap.setPaintProperty(layer.id, 'line-opacity', 0.3);
          }
        }

        if (layer.type === 'symbol') {
          if (layer.layout && layer.layout['text-field']) {
            if (layer.id.includes('place') || layer.id.includes('city') || layer.id.includes('town')) {
              newMap.setPaintProperty(layer.id, 'text-color', '#5ec8e5');
              newMap.setPaintProperty(layer.id, 'text-halo-color', '#0a1628');
              newMap.setPaintProperty(layer.id, 'text-halo-width', 2);
            } else {
              newMap.setPaintProperty(layer.id, 'text-color', '#4a6a7a');
              newMap.setPaintProperty(layer.id, 'text-halo-color', '#0a1628');
              newMap.setPaintProperty(layer.id, 'text-halo-width', 1.5);
            }
          }
        }

        if (layer.type === 'fill-extrusion' || (layer.type === 'fill' && layer.id.includes('building'))) {
          newMap.setPaintProperty(layer.id, 'fill-color', '#2a3a4a');
          newMap.setPaintProperty(layer.id, 'fill-opacity', 0.8);
          newMap.setPaintProperty(layer.id, 'fill-outline-color', '#3a4a5a');
        }
      });
    });

    mapInstance.current = newMap;

    // Ensure map resizes with viewport/URL bar changes to avoid black bands
    const handleResize = () => {
      try {
        newMap.resize();
      } catch {
        // ignore resize errors during teardown
      }
    };

    const resizeObserver = new ResizeObserver(() => handleResize());
    resizeObserver.observe(mapContainer.current);

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', handleResize);
      vv.addEventListener('scroll', handleResize);
    }

    requestAnimationFrame(handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (vv) {
        vv.removeEventListener('resize', handleResize);
        vv.removeEventListener('scroll', handleResize);
      }
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [mapboxToken]);

  useEffect(() => {
    if (!mapInstance.current || !userLocation) return;

    const userEl = document.createElement('div');
    userEl.style.width = '14px';
    userEl.style.height = '14px';
    userEl.style.backgroundColor = '#ef4444';
    userEl.style.borderRadius = '50%';
    userEl.style.boxShadow = '0 0 20px rgba(239, 68, 68, 0.9), 0 0 40px rgba(239, 68, 68, 0.5)';
    userEl.style.cursor = 'pointer';

    new mapboxgl.Marker({ element: userEl, anchor: 'center' })
      .setLngLat(userLocation)
      .addTo(mapInstance.current);
  }, [userLocation, mapboxToken]);

  useEffect(() => {
    if (!mapInstance.current || spotMap.size === 0) return;

    spotMap.forEach((courts, spotId) => {
      if (courts.length === 0) return;

      const firstCourt = courts[0];
      if (!firstCourt.coords_json?.lat || !firstCourt.coords_json?.lng) return;

      const el = document.createElement('div');
      el.style.width = '32px';
      el.style.height = '32px';
      el.style.backgroundColor = '#6E2BFF';
      el.style.borderRadius = '50%';
      el.style.boxShadow = '0 0 20px rgba(110, 43, 255, 0.9), 0 0 40px rgba(110, 43, 255, 0.4), inset 0 0 10px rgba(255, 255, 255, 0.2)';
      el.style.cursor = 'pointer';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      el.style.fontWeight = 'bold';
      el.style.fontSize = '13px';
      el.style.color = 'white';
      el.style.userSelect = 'none';
      el.style.transition = 'box-shadow 0.2s ease';

      if (courts.length > 1) el.textContent = courts.length;

      el.addEventListener('mouseenter', () => {
        el.style.boxShadow = '0 0 30px rgba(110, 43, 255, 1), 0 0 60px rgba(110, 43, 255, 0.6), inset 0 0 15px rgba(255, 255, 255, 0.3)';
      });
      el.addEventListener('mouseleave', () => {
        el.style.boxShadow = '0 0 20px rgba(110, 43, 255, 0.9), 0 0 40px rgba(110, 43, 255, 0.4), inset 0 0 10px rgba(255, 255, 255, 0.2)';
      });

      new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([firstCourt.coords_json.lng, firstCourt.coords_json.lat])
        .addTo(mapInstance.current);

      el.addEventListener('click', () => {
        if (courts.length === 1) {
          mapInstance.current.flyTo({
            center: [firstCourt.coords_json.lng, firstCourt.coords_json.lat],
            zoom: 17,
            duration: 600
          });
          setTimeout(() => navigate(`/court/${courts[0].id}`), 650);
        } else {
          mapInstance.current.flyTo({
            center: [firstCourt.coords_json.lng, firstCourt.coords_json.lat],
            zoom: 18,
            duration: 600
          });
          setTimeout(() => {
            setSelectedSpot(spotId);
            setShowList(false);
          }, 650);
        }
      });
    });
  }, [spotMap, mapboxToken, navigate]);

  /**
   * ------------------------------------------------------------------
   * ChallengeNotification handlers
   * ------------------------------------------------------------------
   */
  const handleDismissNotification = (challengeId) => {
    if (challengeId && user?.id) {
      setDismissedToday(user.id, challengeId);
    }
    setShowChallengeNotif(false);
    setNotifChallenge(null);
  };

  const handleNotificationViewed = (challengeId) => {
    if (!challengeId || !user?.id) return;
    markChallengesAsViewed([challengeId]);
    setDismissedToday(user.id, challengeId);
  };

  return (
    <div className="h-full flex flex-col relative">
      {/* Mobile Fixed Buttons */}
      <div className="md:hidden fixed top-4 right-4 z-30 flex gap-2">
        <Button
          className="bg-[#13151D] text-white shadow-lg rounded-full w-11 h-11 p-0"
          title="Ajouter un terrain"
          onClick={() => navigate('/add-playground')}
        >
          <Plus className="w-5 h-5" />
        </Button>

        <Button
          onClick={() => {
            setShowList(!showList);
            setSelectedSpot(null);
          }}
          className="bg-[#13151D] text-white shadow-lg rounded-full w-11 h-11 p-0"
          title="Liste des terrains"
        >
          <List className="w-5 h-5" />
        </Button>

        {user && (
          <Button
            className="bg-[#13151D] text-white shadow-lg rounded-full w-11 h-11 p-0 relative"
            title="Notifications"
            onClick={() => navigate('/notifications')}
          >
            <Bell className="w-5 h-5" />
            {notifUnreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                {notifUnreadCount > 9 ? '9+' : notifUnreadCount}
              </span>
            )}
          </Button>
        )}

        {!user && (
          <Button
            onClick={() => navigate('/login')}
            className="bg-[#13151D] text-white shadow-lg rounded-full w-11 h-11 p-0"
            title="Connexion"
          >
            <LogIn className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block bg-[#0B0D12] border-b border-[#13151D] text-white p-4 shadow-md z-10">
        <div className="flex items-center justify-between gap-2 md:gap-4">
          <img
            src="https://jszqmvsvkasqsnxkhqor.supabase.co/storage/v1/object/public/playground-photos/zno-logo.png"
            alt="ZNO-Court"
            className="h-8 md:h-10"
          />
          <div className="flex gap-2 md:gap-4 items-center flex-1 justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:text-[#6E2BFF] transition-colors p-2"
              onClick={() => navigate('/add-playground')}
            >
              <Plus className="w-5 h-5" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowList(!showList);
                setSelectedSpot(null);
              }}
              className="text-white hover:text-[#6E2BFF] transition-colors p-2"
            >
              <List className="w-5 h-5" />
            </Button>

            {user ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:text-[#6E2BFF] transition-colors p-2 relative"
                  onClick={() => navigate('/notifications')}
                >
                  <Bell className="w-5 h-5" />
                  {notifUnreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-0.5">
                      {notifUnreadCount > 9 ? '9+' : notifUnreadCount}
                    </span>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:text-[#6E2BFF] transition-colors p-2 text-xs md:text-sm"
                  onClick={() => navigate('/profile')}
                >
                  {user.full_name?.split(' ')[0] || user.email.split('@')[0]}
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/login')}
                className="text-white hover:text-[#6E2BFF] transition-colors p-2 text-xs"
              >
                <LogIn className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Open Duels Button - Fixed on map */}
      {user && openDuelsCount > 0 && (
        <div
          className="fixed right-4 z-20"
          style={{ bottom: OPEN_DUELS_BTN_BOTTOM }}
        >
          {showFindDuelsHint && (
            <div
              onClick={() => {
                setShowFindDuelsHint(false);
                try {
                  localStorage.setItem('zno:seenFindDuelsHint', 'true');
                } catch {
                  // localStorage not available
                }
              }}
              className="absolute -top-14 right-0 z-30 bg-[#13151D] text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-xl border border-[#6E2BFF]/40 cursor-pointer select-none pointer-events-auto whitespace-nowrap"
              style={{ maxWidth: '220px' }}
            >
              Trouve un duel autour de toi
              <div className="absolute -bottom-2 right-4 w-0 h-0 border-l-6 border-r-6 border-t-8 border-l-transparent border-r-transparent border-t-[#13151D]" />
            </div>
          )}
          <button
            onClick={() => navigate('/open-duels')}
            className="relative flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-amber-600 to-amber-500 text-white shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 border-2 border-amber-500/30"
            style={{
              boxShadow: '0 0 25px rgba(217, 119, 6, 0.5), 0 4px 15px rgba(0, 0, 0, 0.3)'
            }}
            title="Duels Ouverts"
          >
            <Swords className="w-5 h-5 drop-shadow-lg" />
            <span className="absolute -top-1 -right-1 bg-gradient-to-br from-red-500 to-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg border border-white/20 animate-pulse">
              {openDuelsCount}
            </span>
          </button>
        </div>
      )}

      {/* Map */}
      <div className="flex-1 relative">
        <div
          ref={mapContainer}
          className="absolute inset-0"
          onClick={(e) => {
            if (e.target === mapContainer.current || e.target.classList.contains('mapboxgl-canvas')) {
              if (showList || selectedSpot) {
                setShowList(false);
                setSelectedSpot(null);
              }
            }
          }}
        />

      {/* Find Duels Location Button */}
      {userLocation && (
          <div
            className="fixed right-4 z-10"
            style={{ bottom: LOCATION_BTN_BOTTOM }}
          >
            {showFindDuelsHint && openDuelsCount === 0 && (
              <div
                onClick={() => {
                  setShowFindDuelsHint(false);
                  try {
                    localStorage.setItem('zno:seenFindDuelsHint', 'true');
                  } catch {
                    // localStorage not available
                  }
                }}
                className="absolute -top-14 right-0 z-30 bg-[#13151D] text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-xl border border-[#6E2BFF]/40 cursor-pointer select-none pointer-events-auto whitespace-nowrap"
                style={{ maxWidth: '220px' }}
              >
                Trouve un duel autour de toi
                <div className="absolute -bottom-2 right-4 w-0 h-0 border-l-6 border-r-6 border-t-8 border-l-transparent border-r-transparent border-t-[#13151D]" />
              </div>
            )}
            <Button
              onClick={() => {
                if (mapInstance.current && userLocation) {
                  mapInstance.current.flyTo({ center: userLocation, zoom: 14 });
                }
              }}
              className="bg-white hover:bg-gradient-to-br hover:from-gray-50 hover:to-white text-gray-800 hover:text-black shadow-xl hover:shadow-2xl hover:scale-110 rounded-full w-10 h-10 p-0 transition-all duration-300 border-2 border-gray-200/50"
              style={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 0 15px rgba(255, 255, 255, 0.3)'
              }}
              title="Trouver des duels autour de moi"
            >
              <Swords className="w-5 h-5 drop-shadow-md" />
            </Button>
          </div>
        )}

        {!mapboxToken && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement de la carte...</p>
            </div>
          </div>
        )}

        {/* List View Bottom Sheet */}
        {showList && (
          <div className="absolute bottom-0 left-0 right-0 bg-[#0B0D12] rounded-t-3xl shadow-2xl z-20 max-h-[calc(100vh-60px)] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-bold text-white">Terrains</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowList(false)} className="p-1">
                  <X className="w-5 h-5 text-white" />
                </Button>
              </div>
              <div className="space-y-3">
                {Array.from(spotMap.entries()).map(([spotId, courts]) => (
                  <div key={spotId}>
                    {courts.map((playground) => (
                      <Card
                        key={playground.id}
                        className="p-4 cursor-pointer hover:bg-[#1B1E28] transition-all"
                        onClick={() => {
                          navigate(`/court/${playground.id}`);
                          setShowList(false);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          {(playground.photo_urls?.[0] || playground.photo_url) ? (
                            <img
                              src={playground.photo_urls?.[0] || playground.photo_url}
                              alt={playground.name}
                              className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-[#6E2BFF]/30"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-[#1B1E28] flex items-center justify-center flex-shrink-0 border border-[#6E2BFF]/30">
                              <MapPin className="w-5 h-5 text-[#6E2BFF]" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-white text-base truncate">{playground.name}</h3>
                            <p className="text-sm text-[#9CA0AB] mt-1 truncate">
                              {playground.city}
                              {courts.length > 1 && <span className="ml-2">· {courts.length} terrains</span>}
                            </p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Spot Selection Sheet - Multiple Courts */}
        {selectedSpot && !showList && spotMap.get(selectedSpot)?.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 bg-[#0B0D12] rounded-t-3xl shadow-2xl z-20 max-h-[70vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-lg font-semibold flex-1 text-white">Sélectionner un terrain</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedSpot(null)}
                  className="text-white hover:text-[#6E2BFF]"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="space-y-3">
                {spotMap.get(selectedSpot).map((court) => (
                  <Card
                    key={court.id}
                    className="p-4 cursor-pointer hover:bg-[#1B1E28] transition-all"
                    onClick={() => {
                      navigate(`/court/${court.id}`);
                      setSelectedSpot(null);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      {(court.photo_urls?.[0] || court.photo_url) ? (
                        <img
                          src={court.photo_urls?.[0] || court.photo_url}
                          alt={court.name}
                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-[#6E2BFF]/30"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-[#1B1E28] flex items-center justify-center flex-shrink-0 border border-[#6E2BFF]/30">
                          <MapPin className="w-5 h-5 text-[#6E2BFF]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-white truncate">{court.name}</h3>
                        <p className="text-sm text-[#B3B6C2] mt-1">Appuyer pour sélectionner</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Challenge Notification */}
      {showChallengeNotif && notifChallenge && (
        <ChallengeNotification
          challenge={notifChallenge}
          onDismiss={handleDismissNotification}
          onViewed={handleNotificationViewed}
        />
      )}
    </div>
  );
}
